<template>
	<div class="colorPickerSheetBody">
		<div
			class="colorPickerSheetBody__label"
			v-if="label"
		>
			{{ label }}
		</div>

		<div class="colorPickerSheetBody__previewRow">
			<div
				:style="{ background: safeHex }"
				class="colorPickerSheetBody__preview"
			/>
			<InputText
				:aria-label="$t('settings_theme_button_color_mode_title')"
				:model-value="hexInput"
				@update:model-value="onHexInput"
				class="colorPickerSheetBody__hexInput"
				maxlength="7"
				spellcheck="false"
			/>
		</div>

		<div class="colorPickerSheetBody__swatches">
			<button
				:aria-label="swatch"
				:aria-pressed="normalize(swatch) === safeHex"
				:class="[
					'colorPickerSheetBody__swatch',
					{ 'colorPickerSheetBody__swatch--active': normalize(swatch) === safeHex },
				]"
				:key="swatch"
				:style="{ background: swatch }"
				:title="swatch"
				@click="onSwatchClick(swatch)"
				type="button"
				v-for="swatch in swatchColors"
			/>
		</div>

		<label class="colorPickerSheetBody__native">
			<input
				:value="safeHex"
				@input="onNativePick"
				class="colorPickerSheetBody__nativeInput"
				type="color"
			/>
			<i class="fa-regular fa-palette" />
			<span>{{ $t('settings_theme_custom_color_label') }}</span>
		</label>
	</div>
</template>

<script lang="ts">
import { PropType, computed, defineComponent, ref, watch } from 'vue';

import InputText from 'primevue/inputtext';

export default defineComponent({
	components: { InputText },
	emits: ['update:modelValue'],
	name: 'ColorPickerSheetBody',
	props: {
		label: { default: null, required: false, type: String },
		modelValue: { required: true, type: String },
		swatchColors: { required: true, type: Array as PropType<string[]> },
	},
	setup(props, { emit }) {
		const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

		const hexInput = ref(props.modelValue);

		watch(
			() => props.modelValue,
			(next) => {
				hexInput.value = next;
			}
		);

		const safeHex = computed((): string => normalize(props.modelValue));

		function normalize(value: string): string {
			const trimmed = value.trim();
			const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;

			return withHash.toLowerCase();
		}

		function onSwatchClick(swatch: string): void {
			const next = normalize(swatch);
			hexInput.value = next;
			emit('update:modelValue', next);
		}

		function onHexInput(next: null | string): void {
			const raw = next ?? '';
			hexInput.value = raw;

			const normalized = normalize(raw);

			if (HEX_PATTERN.test(normalized)) {
				emit('update:modelValue', normalized);
			}
		}

		function onNativePick(event: Event): void {
			const target = event.target as HTMLInputElement;
			const next = normalize(target.value);

			hexInput.value = next;
			emit('update:modelValue', next);
		}

		return { hexInput, normalize, onHexInput, onNativePick, onSwatchClick, safeHex };
	},
});
</script>

<style lang="scss" scoped>
.colorPickerSheetBody {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.colorPickerSheetBody__label {
	color: var(--p-text-color);
	font-size: inherit;
	font-weight: 600;
}

.colorPickerSheetBody__previewRow {
	align-items: center;
	display: flex;
	gap: 10px;
}

.colorPickerSheetBody__preview {
	border: 1px solid var(--p-surface-300);
	border-radius: var(--radius-md, 8px);
	flex: 0 0 auto;
	height: 36px;
	width: 36px;
}

.body--dark .colorPickerSheetBody__preview {
	border-color: var(--p-surface-600);
}

.colorPickerSheetBody__hexInput {
	flex: 1;
	font-family: var(--font-mono, monospace);
	font-size: var(--text-sm);
}

.colorPickerSheetBody__swatches {
	display: grid;
	gap: 8px;
	grid-template-columns: repeat(7, 1fr);
}

.colorPickerSheetBody__swatch {
	aspect-ratio: 1;
	border: 2px solid transparent;
	border-radius: 50%;
	cursor: pointer;
	min-height: 28px;
	padding: 0;
	transition:
		border-color 120ms ease,
		transform 120ms ease;
}

.colorPickerSheetBody__swatch:hover {
	transform: scale(1.08);
}

.colorPickerSheetBody__swatch--active {
	border-color: var(--p-surface-900);
}

.body--dark .colorPickerSheetBody__swatch--active {
	border-color: var(--p-surface-0);
}

.colorPickerSheetBody__native {
	align-items: center;
	background: var(--p-surface-100);
	border: 1px dashed var(--p-surface-300);
	border-radius: var(--radius-md, 8px);
	color: var(--p-text-color);
	cursor: pointer;
	display: inline-flex;
	font-size: var(--text-sm);
	gap: 8px;
	justify-content: center;
	min-height: 38px;
	padding: 8px 12px;
	transition: background-color 120ms ease;
}

.colorPickerSheetBody__native:hover {
	background: var(--hover-light);
}

.body--dark .colorPickerSheetBody__native {
	background: var(--p-surface-800);
	border-color: var(--p-surface-600);
}

.body--dark .colorPickerSheetBody__native:hover {
	background: var(--hover-dark);
}

.colorPickerSheetBody__nativeInput {
	height: 0;
	opacity: 0;
	pointer-events: none;
	position: absolute;
	width: 0;
}

@media (max-width: 480px) {
	.colorPickerSheetBody__swatches {
		grid-template-columns: repeat(6, 1fr);
	}

	.colorPickerSheetBody__swatch {
		min-height: 44px;
	}
}
</style>
